import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    X, Heart, MessageCircle, Share2, ThumbsUp, MoreHorizontal, Image, Loader2, AlertCircle, CheckCircle, Save, RotateCcw, FileText, Globe
} from 'lucide-react';
import vaultService from '../../../services/vaultService';
import {
    FACEBOOK_TEMPLATES,
    FACEBOOK_TONE_OPTIONS,
    FACEBOOK_CTA_OPTIONS,
    FACEBOOK_QUICK_ACTIONS,
    OPTIMAL_LENGTHS
} from '../../../ai/prompts/contentPrompts';

const FACEBOOK_OPTIMAL_LENGTH = OPTIMAL_LENGTHS.facebook;
const TONE_OPTIONS = FACEBOOK_TONE_OPTIONS;
const CTA_OPTIONS = FACEBOOK_CTA_OPTIONS;
const QUICK_ACTIONS = FACEBOOK_QUICK_ACTIONS;
const buildFacebookPrompt = (params) => params.basePrompt || '';
const parseFacebookPost = (content) => ({ text: content, media: null });
const formatFacebookPost = (post) => post.text || post;
const validateFacebookPost = (content) => ({ isValid: true, errors: [], warnings: [] });

/**
 * FacebookPreview - Dynamic Facebook post preview component
 */
const FacebookPreview = ({
    content = null,
    isStreaming = false,
    guestName = null,
    isEditable = false,
    onContentChange = null,
    hasUnsavedChanges = false,
    onSave = null,
    media = null,
    onMediaChange = null,
    isUploading = false,
    episode = null,
    onSaveToVault = null,
    onStartOver = null,
    isSavingToVault = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [isDraggingImage, setIsDraggingImage] = useState(false);
    const [isDraggingText, setIsDraggingText] = useState(false);
    const [isInternalUploading, setIsInternalUploading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    const imageDragCounterRef = useRef(0);
    const textDragCounterRef = useRef(0);

    const defaultContent = `We're excited to announce our latest feature on PodGuestly! 

AI-powered repurposing is here to change the game. Turn your audio content into social media gold in seconds.

Check it out and let us know what you think!

#Podcasting #AI #ContentCreation`;

    const displayContent = content || defaultContent;

    const charCount = useMemo(() => displayContent.length, [displayContent]);
    const validation = useMemo(() => validateFacebookPost(displayContent), [displayContent]);

    const formatContent = (text) => {
        if (!text) return null;
        const parts = text.split(/(#[\w]+)/g);
        return parts.map((part, index) => {
            if (part.startsWith('#')) {
                return <span key={index} className="text-blue-600 font-normal hover:underline cursor-pointer">{part}</span>;
            }
            return part;
        });
    };

    const handleTextChange = (e) => {
        if (onContentChange) onContentChange(e.target.value);
    };

    const handleContentClick = () => {
        if (isEditable && !isStreaming) setIsEditing(true);
    };

    const handleBlur = () => setIsEditing(false);

    useEffect(() => {
        if (isEditing && textareaRef.current) textareaRef.current.focus();
    }, [isEditing]);

    const handleFileSelect = async (file) => {
        if (!file) return;
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            setUploadError('Please select a valid image file (JPG, PNG, GIF, or WebP)');
            return;
        }
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            setUploadError('Image size must be less than 5MB');
            return;
        }
        setUploadError(null);
        if (onMediaChange) {
            await onMediaChange(file);
        } else {
            setIsInternalUploading(true);
            try {
                const result = await vaultService.uploadFile(file, {
                    episodeId: episode?.id,
                    tags: ['facebook', 'social-media', 'content-creation'],
                    description: `Facebook post image for ${episode?.title || 'content'}`
                });
                const asset = result.asset || result;
                onMediaChange({
                    url: asset.downloadUrl || asset.url || asset.fileUrl,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    id: asset.id || asset.assetId
                });
            } catch (error) {
                console.error('Failed to upload media:', error);
                setUploadError('Failed to upload image. Please try again.');
            } finally {
                setIsInternalUploading(false);
            }
        }
    };

    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) handleFileSelect(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleImageDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        imageDragCounterRef.current++;
        if (imageDragCounterRef.current === 1) {
            if (e.dataTransfer.types.includes('application/json') || e.dataTransfer.types.includes('Files')) {
                setIsDraggingImage(true);
            }
        }
    };

    const handleImageDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        imageDragCounterRef.current--;
        if (imageDragCounterRef.current === 0) setIsDraggingImage(false);
    };

    const handleImageDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleImageDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        imageDragCounterRef.current = 0;
        setIsDraggingImage(false);

        if (e.dataTransfer.types.includes('application/json')) {
            try {
                const jsonData = e.dataTransfer.getData('application/json');
                const artifact = JSON.parse(jsonData);
                if (artifact && artifact.type === 'vault-asset' && artifact.assetType === 'image') {
                    onMediaChange({
                        url: artifact.downloadUrl,
                        name: artifact.fileName || artifact.title,
                        type: artifact.mimeType || 'image/jpeg',
                        size: artifact.size,
                        id: artifact.id
                    });
                    return;
                } else if (artifact && artifact.type === 'vault-asset' && artifact.assetType !== 'image') {
                    setUploadError('Only images can be dropped here. Text should be dropped on the text area.');
                    setTimeout(() => setUploadError(null), 3000);
                    return;
                }
            } catch (err) {
                console.error('Failed to parse vault asset:', err);
            }
        }

        const file = e.dataTransfer.files?.[0];
        if (file) {
            const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                setUploadError('Only image files can be dropped here. Text should be dropped on the text area.');
                setTimeout(() => setUploadError(null), 3000);
                return;
            }
            handleFileSelect(file);
        }
    };

    const handleTextDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        textDragCounterRef.current++;
        if (textDragCounterRef.current === 1) {
            if (e.dataTransfer.types.includes('application/json') || e.dataTransfer.types.includes('text/plain')) {
                setIsDraggingText(true);
            }
        }
    };

    const handleTextDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        textDragCounterRef.current--;
        if (textDragCounterRef.current === 0) setIsDraggingText(false);
    };

    const handleTextDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleTextDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        textDragCounterRef.current = 0;
        setIsDraggingText(false);

        if (e.dataTransfer.types.includes('application/json')) {
            try {
                const jsonData = e.dataTransfer.getData('application/json');
                const artifact = JSON.parse(jsonData);
                if (artifact && artifact.type === 'vault-asset' && artifact.assetType === 'text' && artifact.content) {
                    if (onContentChange) onContentChange(artifact.content);
                    return;
                } else if (artifact && artifact.type === 'vault-asset' && artifact.assetType !== 'text') {
                    setUploadError('Only text content can be dropped here. Images should be dropped on the image area.');
                    setTimeout(() => setUploadError(null), 3000);
                    return;
                }
            } catch (err) {
                console.error('Failed to parse vault asset:', err);
            }
        }

        if (e.dataTransfer.types.includes('text/plain')) {
            const text = e.dataTransfer.getData('text/plain');
            if (text && onContentChange) onContentChange(text);
        }
    };

    const handleRemoveImage = () => {
        if (onMediaChange) onMediaChange(null);
        setUploadError(null);
    };

    const handleUploadClick = () => fileInputRef.current?.click();

    const showUploading = isUploading || isInternalUploading;

    return (
        <div className="flex flex-col h-full font-sans items-center pt-4">
            {isEditable && content && (
                <div className="w-full max-w-[550px] mb-2 px-2">
                    <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-3 py-2">
                        <div className="flex items-center gap-3">
                            <div className={`flex items-center gap-1 text-xs font-medium ${validation.warnings.length > 0 ? 'text-amber-500' : 'text-gray-500'}`}>
                                <span>{charCount.toLocaleString()}</span>
                                <span>/</span>
                                <span>{FACEBOOK_OPTIMAL_LENGTH.absoluteMax.toLocaleString()}</span>
                            </div>
                            <div className="w-px h-4 bg-gray-200" />
                            {saveSuccess ? (
                                <>
                                    <CheckCircle size={14} className="text-green-500" />
                                    <span className="text-xs text-green-600 font-medium">Saved to Vault!</span>
                                </>
                            ) : hasUnsavedChanges ? (
                                <>
                                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                    <span className="text-xs text-amber-600 font-medium">Unsaved</span>
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={14} className="text-green-500" />
                                    <span className="text-xs text-green-600 font-medium">Ready to save</span>
                                </>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={onStartOver} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-all">
                                <RotateCcw size={14} />
                                Start Over
                            </button>
                            <button
                                onClick={async () => {
                                    if (onSaveToVault) {
                                        const success = await onSaveToVault();
                                        if (success) {
                                            setSaveSuccess(true);
                                            setTimeout(() => setSaveSuccess(false), 2000);
                                        }
                                    }
                                }}
                                disabled={isSavingToVault || !content}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isSavingToVault ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'}`}
                            >
                                {isSavingToVault ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={14} />
                                        Save to Vault
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="w-full max-w-full overflow-y-auto scrollbar-hide px-0">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mx-auto max-w-[550px]">
                    <div className="px-4 py-3 flex items-start justify-between">
                        <div className="flex gap-2.5">
                            <div className="relative">
                                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                                    <span className="text-white font-bold text-xs">PG</span>
                                </div>
                                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white"></div>
                            </div>
                            <div>
                                <h3 className="text-[15px] font-semibold text-gray-900 leading-tight hover:underline cursor-pointer">PodGuestly</h3>
                                <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-500">
                                    <span className="hover:underline cursor-pointer">2h</span>
                                    <span>-</span>
                                    <Globe size={10} className="text-gray-500" />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <MoreHorizontal size={20} className="text-gray-500 hover:bg-gray-100 rounded-full p-0.5 box-content cursor-pointer transition-colors" />
                            <X size={20} className="text-gray-500 hover:bg-gray-100 rounded-full p-0.5 box-content cursor-pointer transition-colors" />
                        </div>
                    </div>

                    <div
                        className="px-4 pb-2"
                        onDragEnter={handleTextDragEnter}
                        onDragLeave={handleTextDragLeave}
                        onDragOver={handleTextDragOver}
                        onDrop={handleTextDrop}
                    >
                        {isEditing && isEditable && !isStreaming ? (
                            <textarea
                                ref={textareaRef}
                                value={displayContent}
                                onChange={handleTextChange}
                                onBlur={handleBlur}
                                className={`w-full text-[15px] text-gray-900 leading-relaxed whitespace-pre-wrap resize-none border transition-all outline-none rounded-lg p-2 -mx-2 -my-2 ${isDraggingText ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-blue-300 focus:ring-2 focus:ring-blue-100'}`}
                                placeholder="What's on your mind?"
                                rows={displayContent.split('\n').length + 2}
                            />
                        ) : (
                            <div
                                onClick={handleContentClick}
                                className={`text-[15px] text-gray-900 leading-relaxed whitespace-pre-wrap transition-all rounded-lg ${isDraggingText ? 'bg-blue-50 border-2 border-dashed border-blue-400 p-8 min-h-[120px] flex items-center justify-center' : 'p-2 -mx-2 -my-2'} ${isEditable && !isStreaming ? 'cursor-text hover:bg-gray-50' : ''}`}
                            >
                                {isDraggingText ? (
                                    <div className="text-center">
                                        <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-blue-100 flex items-center justify-center">
                                            <FileText size={24} className="text-blue-600" />
                                        </div>
                                        <span className="text-blue-600 font-semibold text-base">Drop text content here</span>
                                        <p className="text-blue-400 text-xs mt-1">Release to insert text</p>
                                    </div>
                                ) : (
                                    <p className="whitespace-pre-wrap leading-relaxed">
                                        {formatContent(displayContent)}
                                        {isStreaming && <span className="inline-block w-1.5 h-4 bg-blue-500 animate-pulse ml-0.5" />}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <div
                        className={`w-full bg-gray-100 overflow-hidden group border-t border-b border-gray-200 ${!media && !showUploading ? 'cursor-pointer' : ''}`}
                        onClick={!media && !showUploading ? handleUploadClick : undefined}
                        onDragEnter={handleImageDragEnter}
                        onDragLeave={handleImageDragLeave}
                        onDragOver={handleImageDragOver}
                        onDrop={handleImageDrop}
                    >
                        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleInputChange} className="hidden" />

                        {showUploading ? (
                            <div className="aspect-[1.91/1] bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                                <div className="text-center">
                                    <div className="mx-auto w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center mb-2">
                                        <Loader2 size={24} className="text-blue-600 animate-spin" />
                                    </div>
                                    <p className="text-sm font-semibold text-gray-700">Uploading...</p>
                                </div>
                            </div>
                        ) : media ? (
                            <div className="aspect-[1.91/1] relative bg-gray-100 flex items-center justify-center overflow-hidden group">
                                <img src={media.url} alt="Uploaded media" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                <div className="absolute inset-0 bg-gray-200 flex items-center justify-center" style={{ display: 'none' }}>
                                    <div className="text-center">
                                        <AlertCircle size={24} className="text-gray-400 mx-auto mb-2" />
                                        <p className="text-sm text-gray-500">Failed to load image</p>
                                    </div>
                                </div>
                                <button onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }} className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition-colors opacity-0 group-hover:opacity-100" title="Remove image">
                                    <X size={16} />
                                </button>
                            </div>
                        ) : (
                            <div className={`aspect-[1.91/1] bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center transition-colors ${isDraggingImage ? 'bg-blue-100' : ''}`}>
                                <div className="text-center">
                                    <div className={`mx-auto w-12 h-12 rounded-lg shadow-sm flex items-center justify-center mb-2 transition-colors ${isDraggingImage ? 'bg-blue-100' : 'bg-white'}`}>
                                        <Image size={24} className={`${isDraggingImage ? 'text-blue-600' : 'text-gray-400'}`} />
                                    </div>
                                    <p className="text-sm font-semibold text-gray-700">{isDraggingImage ? 'Drop image here' : (guestName ? `Episode with ${guestName}` : 'Add Photo')}</p>
                                    <p className="text-xs text-gray-500 mt-1">Click or drag to upload</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {uploadError && (
                        <div className="px-4 py-2">
                            <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 px-3 py-2 rounded-lg">
                                <AlertCircle size={14} />
                                <span>{uploadError}</span>
                            </div>
                        </div>
                    )}

                    <div className="px-4 py-2.5 flex items-center justify-between border-b border-gray-200">
                        <div className="flex items-center gap-1.5 cursor-pointer hover:underline decoration-gray-500/50">
                            <div className="flex -space-x-1.5 items-center">
                                <div className="w-[18px] h-[18px] z-30 rounded-full bg-blue-500 flex items-center justify-center ring-2 ring-white">
                                    <ThumbsUp size={10} className="text-white fill-white" />
                                </div>
                                <div className="w-[18px] h-[18px] z-20 rounded-full bg-red-500 flex items-center justify-center ring-2 ring-white">
                                    <Heart size={10} className="text-white fill-white" />
                                </div>
                            </div>
                            <span className="text-[15px] text-gray-500 hover:text-blue-600">247</span>
                        </div>
                        <div className="flex items-center gap-3 text-[15px] text-gray-500">
                            <span className="hover:underline cursor-pointer">48 comments</span>
                            <span className="hover:underline cursor-pointer">12 shares</span>
                        </div>
                    </div>

                    <div className="px-2 py-1 flex items-center justify-between gap-1">
                        <button className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md hover:bg-gray-100/80 transition-colors text-gray-600 font-medium text-[15px]">
                            <ThumbsUp size={20} />
                            <span>Like</span>
                        </button>
                        <button className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md hover:bg-gray-100/80 transition-colors text-gray-600 font-medium text-[15px]">
                            <MessageCircle size={20} />
                            <span>Comment</span>
                        </button>
                        <button className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md hover:bg-gray-100/80 transition-colors text-gray-600 font-medium text-[15px]">
                            <Share2 size={20} />
                            <span>Share</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { FacebookPreview };
export { FACEBOOK_TEMPLATES, TONE_OPTIONS, CTA_OPTIONS, QUICK_ACTIONS, buildFacebookPrompt };
export default FacebookPreview;
